import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { takeUntil } from 'rxjs/operators';
import { BehaviorSubject, Subject } from 'rxjs';
import { AddOrgParams } from '../../domain/models/admin';
import { IAdminModalComponent, IAdminModalData } from '../../domain/models/admin-modal';
import { AdminOrganizationsService } from '../../services/admin-orgs.service';
import { IProcessStatus } from '@app/interfaces/process.interface';

@Component({
	selector: 'app-admin-create-org-modal',
	templateUrl: './admin-create-org-modal.component.html',
	styleUrls: ['./admin-create-org-modal.component.scss'],
})
export class AdminCreateOrgModalComponent implements IAdminModalComponent, OnInit, OnDestroy {
	@Input() data: IAdminModalData<{}>;
	private entity: AddOrgParams;
	private readonly unsubscribe$: Subject<boolean> = new Subject<boolean>();
	public form: FormGroup;
	public initProcess = false;
	public processStatus: BehaviorSubject<IProcessStatus> = new BehaviorSubject<IProcessStatus>(IProcessStatus.loading);

	constructor(public fb: FormBuilder, public adminOrgsService: AdminOrganizationsService) {}

	ngOnInit(): void {
		this.initForm();
	}

	ngOnDestroy(): void {
		this.unsubscribe$.next(true);
		this.unsubscribe$.complete();
	}

	private initForm(): void {
		this.entity = this.createNewEntity();
		this.form = this.createForm(this.entity);
	}

	private createNewEntity(): AddOrgParams {
		const org = new AddOrgParams();
		org.varible_fee = 15;
		org.charge_model = 'SuccessFee';
		org.create_in_icount = true;
		org.plan_desc = this.getPlanDesc(org.varible_fee);

		return org;
	}

	private createForm(ent: AddOrgParams): FormGroup {
		const form = this.fb.group({
			company: ['', Validators.required],
			vat: [''],
			street: [''],
			city: [''],
			zipcode: [''],
			country: [''],
			contanct_firstName: [''],
			contanct_lastName: [''],
			contanct_email: [''],
			contanct_phone: [''],
			create_in_icount: [ent.create_in_icount],

			charge_model: [ent.charge_model],
			varible_fee: [ent.varible_fee, Validators.required],
			plan_desc: [ent.plan_desc],
		});

		form.get('charge_model').valueChanges.subscribe((charge_model) => this.onChargeType(charge_model));
		form.get('varible_fee').valueChanges.subscribe((varible_fee) => this.onVarFeeChanged(varible_fee));

		return form;
	}

	private onChargeType(chargeType: string): void {
		switch (chargeType) {
			case 'SuccessFee': {
				this.form.controls.varible_fee.setValidators([Validators.required]);
				break;
			}
			case 'FixPrice': {
				this.resetOrgValidator('varible_fee');
				this.resetOrgValidator('plan_desc');
				break;
			}
		}
	}

	private onVarFeeChanged(varFee: number): void {
		this.form.patchValue({ plan_desc: this.getPlanDesc(varFee) });
	}

	private getPlanDesc(varFee: number): string {
		return "You'll be charged " + (varFee || 0) + '% of the saved amount';
	}

	private mapFormToEntity(form: FormGroup, entity: AddOrgParams): AddOrgParams {
		const formValue = form.value;

		entity.company = formValue.company;
		entity.vat = formValue.vat;
		entity.street = formValue.street;
		entity.city = formValue.city;
		entity.zipcode = formValue.zipcode;
		entity.country = formValue.country;
		entity.charge_model = formValue.charge_model;

		entity.varible_fee = formValue.varible_fee;
		entity.plan_desc = formValue.plan_desc ? formValue.plan_desc : '';

		entity.contanct_firstName = formValue.contanct_firstName;
		entity.contanct_lastName = formValue.contanct_lastName;
		entity.contanct_email = formValue.contanct_email;
		entity.contanct_phone = formValue.contanct_phone;
		entity.create_in_icount = formValue.create_in_icount;

		return entity;
	}

	private resetOrgValidator(ent: string): void {
		this.form.get(ent).clearValidators();
		this.form.get(ent).reset();
		this.form.get(ent).updateValueAndValidity();
	}

	private resetForm() {
		this.form.markAsPristine();
		this.form.markAsUntouched();
		this.form.updateValueAndValidity();
		this.form.reset();
	}

	public onSubmit() {
		this.initProcess = true;

		this.mapFormToEntity(this.form, this.entity);

		this.adminOrgsService
			.addOrganization(this.entity)
			.pipe(takeUntil(this.unsubscribe$))
			.subscribe({
				next: (res) => {
					const status = res.success ? IProcessStatus.saved : IProcessStatus.failed;
					const options = { message: status };
					this.processStatus.next(status);
					setTimeout(() => {
						this.closeEvent(options);
					}, 1500);
				},
			});
	}

	public closeEvent(e?: {}): void {
		this.resetForm();
	}
}
